import React from 'react';
import {
  useForm,
  Form,
  Input,
  Edit,
  Col,
  Row,
  RefreshButton,
  ListButton,
  Space,
  Select,
} from '@pankod/refine-antd';
import type { HttpError, OpenNotificationParams } from '@pankod/refine-core';
import { UserTypes } from 'interfaces';
import { USER_ROLES } from 'constant';

const UserEdit: React.FC = () => {
  const { formProps, saveButtonProps, queryResult } = useForm<
    UserTypes,
    HttpError,
    UserTypes
  >({
    metaData: {
      resource: 'users',
      fields: ['id', 'name', 'email', 'role'],
    },
    onMutationSuccess(data, variables, context) {
      return Promise.resolve('/users');
    },

    errorNotification(error: any, variables, context): OpenNotificationParams {
      return {
        message: error?.response?.errors[0]?.message,
        type: 'error',
      };
    },
  });

  const { isLoading }: any = queryResult;

  return (
    <Edit
      isLoading={isLoading}
      saveButtonProps={saveButtonProps}
      headerProps={{
        extra: (
          <Space>
            <ListButton />
            <RefreshButton onClick={() => queryResult?.refetch()} />
          </Space>
        ),
      }}
    >
      <Form
        {...formProps}
        layout='vertical'
        onFinish={async (values: any) =>
          formProps.onFinish?.({
            name: values.name,
            email: values.email,
            role: values.role,
            old_password: values.old_password,
            new_password: values.confirm_password,
          } as any)
        }
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label='Name'
              name='name'
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label='Email'
              name='email'
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label='Role'
              name='role'
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Select
                placeholder='Role'
                options={[
                  { label: 'Super Admin', value: USER_ROLES.SUPER_ADMIN },
                  { label: 'Admin', value: USER_ROLES.ADMIN },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label='Old Password' name='old_password' hasFeedback>
              <Input.Password />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label='New Password'
              dependencies={['old_password']}
              name='new_password'
              hasFeedback
              rules={[
                {
                  pattern:
                    /^(?=.*[0-8])(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.* ).{8,16}$/,
                  message:
                    'Password must contain one digit from 1 to 8, one lowercase letter, one uppercase letter, one special character, no space, and it must be 8-16 characters long.',
                },
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label='Confirm Password'
              name='confirm_password'
              dependencies={['new_password']}
              hasFeedback
              rules={[
                {
                  pattern:
                    /^(?=.*[0-8])(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.* ).{8,16}$/,
                  message:
                    'Password must contain one digit from 1 to 8, one lowercase letter, one uppercase letter, one special character, no space, and it must be 8-16 characters long.',
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('new_password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        'The two passwords that you entered do not match!'
                      )
                    );
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Edit>
  );
};

export default UserEdit;
